import type { DsoCustomerRow, DsoDashboard, DsoTrendPoint } from '@dcc-erp/shared';
import { fmt, fmtB, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

// =============================================================================
// Router-free AR DSO (Days Sales Outstanding) KPI dashboard view (AR-13). The
// route owns data fetching (useDsoDashboard) + loading/error/empty gating + the
// by-customer row navigation; this component owns presentation only:
//   1. a TILES row (Overall DSO / BPDSO / ADD / Total AR / Overdue %),
//   2. a 12-month DSO TREND bar row (lightweight CSS bars — the same
//      .bars/.bcol/.bstack/.b1/.blabel idiom CollectionForecast reuses; no chart
//      dependency), and
//   3. a BY-CUSTOMER table (customer / AR / sales-365d / DSO) sorted slowest
//      first, each row a link to the customer's statement.
// A null `dso` (no trailing-365 sales → divide-by-zero) renders as a dash.
// Plain prototype.css classes only.
// =============================================================================

// A rounded integer day count, or a dash when null (no trailing-365 sales).
function dsoOrDash(dso: number | null | undefined): string {
  return dso === null || dso === undefined ? '—' : fmt(dso);
}

export interface DsoDashboardViewProps {
  data: DsoDashboard;
  /** Navigate to a customer's statement (the by-customer row link). */
  onOpenCustomer: (customerId: string) => void;
}

export function DsoDashboardView({ data, onOpenCustomer }: DsoDashboardViewProps) {
  const totalAR = toNumber(data.totalAR);
  const notDueAR = toNumber(data.notDueAR);
  const overdueAR = toNumber(data.overdueAR);

  // Client-side slowest-first fallback so the row order holds even if the server
  // order changes; nulls (no trailing-365 sales) sort last.
  const byCustomer = [...data.byCustomer].sort((a, b) => (b.dso ?? -1) - (a.dso ?? -1));

  return (
    <>
      {/* ---- TILES -------------------------------------------------------- */}
      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            DSO รวม (Days Sales Outstanding)
          </div>
          <div className="value tnum">
            {dsoOrDash(data.overall.dso)} <span className="unit">วัน</span>
          </div>
          <div className="trend faint">เฉลี่ยระยะเวลาเก็บหนี้ · {data.days} วันย้อนหลัง</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            BPDSO (เก็บหนี้ดีที่สุด)
          </div>
          <div className="value tnum">
            {dsoOrDash(data.bpdso)} <span className="unit">วัน</span>
          </div>
          <div className="trend faint">หนี้ที่ยังไม่ครบกำหนด ÷ ยอดขายเฉลี่ย/วัน</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ADD (วันค้างเฉลี่ย)
          </div>
          <div className="value tnum">
            {dsoOrDash(data.add)} <span className="unit">วัน</span>
          </div>
          <div className="trend faint">DSO − BPDSO · ส่วนที่เก็บช้ากว่ากำหนด</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ลูกหนี้คงค้างรวม
          </div>
          <div className="value tnum">
            {fmtB(totalAR)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">ยอดคงค้าง = รวม − รับชำระ − ลดหนี้</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            เกินกำหนด (% ของคงค้าง)
          </div>
          <div className="value tnum">
            {fmt(data.overduePct, 1)} <span className="unit">%</span>
          </div>
          <div className="trend faint">
            ยังไม่ครบ {fmtB(notDueAR)} · เกินกำหนด {fmtB(overdueAR)} บาท
          </div>
        </div>
      </div>

      {/* ---- 12-MONTH TREND ---------------------------------------------- */}
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>แนวโน้ม DSO (12 เดือน)</h3>
            <div className="sub">ระยะเวลาเก็บหนี้รายเดือน ณ สิ้นเดือน · หน่วย: วัน</div>
          </div>
        </div>
        <div className="card-body">
          <DsoTrend trend={data.trend} />
        </div>
      </div>

      {/* ---- BY-CUSTOMER ------------------------------------------------- */}
      <div className="tbl-shell">
        <div className="tbl-wrap" style={{ maxHeight: 600 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>ลูกค้า</th>
                <th className="num">ลูกหนี้คงค้าง</th>
                <th className="num">ยอดขาย (365 วัน)</th>
                <th className="num">DSO (วัน)</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {byCustomer.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                    ไม่มีลูกหนี้คงค้าง
                  </td>
                </tr>
              ) : (
                byCustomer.map((row: DsoCustomerRow) => (
                  <tr key={row.customerId}>
                    <td>{row.customerName}</td>
                    <td className="num tnum">{fmt(toNumber(row.ar), 2)}</td>
                    <td className="num tnum">{fmt(toNumber(row.sales), 2)}</td>
                    <td className="num tnum">{dsoOrDash(row.dso)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn ghost"
                        style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => onOpenCustomer(row.customerId)}
                        title="ดูใบแจ้งยอดบัญชีลูกหนี้"
                      >
                        <Icon name="reports" size={12} /> ใบแจ้งยอด
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// 12-month DSO trend as a lightweight CSS bar row (one bar per month, height
// proportional to that month's DSO). Reuses the dashboard bar-chart CSS
// (.bars/.bcol/.bstack/.b1/.blabel) — the same idiom CollectionForecast uses, so
// no chart dependency. A null DSO (no sales that month) renders a zero-height bar
// with a "—" cue in the title.
function DsoTrend({ trend }: { trend: DsoTrendPoint[] }) {
  if (trend.length === 0) {
    return (
      <div className="muted" style={{ padding: 20, textAlign: 'center' }}>
        ยังไม่มีข้อมูลแนวโน้ม
      </div>
    );
  }
  // Scale heights to the tallest month so the chart fills its box. Clamp each
  // month's DSO to non-negative first: a negative cumulative-AR month would
  // otherwise yield an invalid CSS percent (collapsing the bar) or skew the
  // scale. Guard against an all-null / all-zero trend so we never divide by zero.
  const maxBar = Math.max(1, ...trend.map((p) => Math.max(0, p.dso ?? 0)));
  return (
    <div className="bars">
      {trend.map((p) => {
        // Non-negative bar value: a null / 0 / negative DSO becomes a tiny
        // visible stub (via minHeight below), never a collapsed or invalid bar.
        const value = Math.max(0, p.dso ?? 0);
        // 'YYYY-MM' → 'MM' for a compact month label.
        const label = p.period.slice(5);
        return (
          <div className="bcol" key={p.period}>
            <div className="bstack">
              <div
                data-testid="dso-bar"
                className="b1"
                // minHeight keeps a 0 / low / null month visible as a stub.
                style={{ height: `${(value / maxBar) * 100}%`, minHeight: 2 }}
                title={`${p.period} · DSO ${dsoOrDash(p.dso)} วัน`}
              />
            </div>
            <div className="blabel">{label}</div>
          </div>
        );
      })}
    </div>
  );
}
